import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
const API = axios.create({ baseURL: API_BASE_URL });

const TOKEN_KEY = 'app.auth.token';

let onUnauthorized = null;

export function setOnUnauthorized(handler) {
  onUnauthorized = handler;
}

export function setAuthToken(token) {
  if (token) {
    API.defaults.headers.common.Authorization = `Bearer ${token}`;
    try { localStorage.setItem(TOKEN_KEY, token); } catch { /* ignore */ }
  } else {
    delete API.defaults.headers.common.Authorization;
    try { localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
  }
}

export function getStoredToken() {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}

const initialToken = getStoredToken();
if (initialToken) {
  API.defaults.headers.common.Authorization = `Bearer ${initialToken}`;
}

API.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401 && onUnauthorized) {
      onUnauthorized();
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (username, password) => API.post('/auth/login', { username, password });
export const fetchMe = () => API.get('/auth/me');
export const changeOwnPassword = (currentPassword, newPassword) =>
  API.post('/auth/change-password', { currentPassword, newPassword });

// Stops (standalone)
export const getStops = () => API.get('/stops');
export const createStop = (data) => API.post('/stops', data);
export const updateStop = (id, data) => API.put(`/stops/${id}`, data);
export const deleteStop = (id) => API.delete(`/stops/${id}`);
export const bulkCreateStops = (stops) => API.post('/stops/bulk', { stops });
export const bulkDeleteStops = (ids) => API.post('/stops/bulk-delete', { ids });

// Bus routes
export const getBusRoutes = () => API.get('/bus-routes');
export const createBusRoute = (data) => API.post('/bus-routes', data);
export const updateBusRoute = (id, data) => API.put(`/bus-routes/${id}`, data);
export const deleteBusRoute = (id) => API.delete(`/bus-routes/${id}`);
export const importBusRoutes = (formData) => API.post('/bus-routes/import', formData);
export const bulkDeleteBusRoutes = (ids) => API.post('/bus-routes/bulk-delete', { ids });
export const getRoutePath = (id) => API.get(`/bus-routes/${id}/path`);
export const getRouteStops = (id) => API.get(`/bus-routes/${id}/stops`);

// Admin / Command Center (super admin only)
export const getAdminOverview = () => API.get('/admin/overview');
export const getUserActivity = (userId) => API.get(`/admin/users/${userId}/activity`);
export const createAdminUser = (data) => API.post('/admin/users', data);
export const updateAdminUser = (userId, data) => API.patch(`/admin/users/${userId}`, data);
export const deleteAdminUser = (userId) => API.delete(`/admin/users/${userId}`);
export const resetUserPassword = (userId, password) =>
  API.post(`/admin/users/${userId}/reset-password`, { password });

export const getVersioningOverview = () => API.get('/admin/versioning/overview');
export const getEntityHistory = (entityId) => API.get(`/admin/versioning/${entityId}/history`);
export const getBatch = (batchId) => API.get(`/admin/batches/${batchId}`);
export const rollbackEntityVersion = (entityId, entityVersionId) =>
  API.post(`/admin/versioning/${entityId}/rollback`, { entityVersionId });

export const getAuditLog = (params) => API.get('/admin/audit', { params });
export const getDataQuality = () => API.get('/admin/data-quality');
export const getInsights = () => API.get('/admin/insights');

// Trash / restore
export const getTrash = () => API.get('/admin/trash');
export const restoreTrashItem = (entityType, id) =>
  API.post(`/admin/trash/${entityType}/${id}/restore`);
export const purgeTrashItem = (entityType, id) =>
  API.delete(`/admin/trash/${entityType}/${id}`);

// GTFS download URL (token attached via header by the axios instance for fetch fallback)
export const exportGtfs = () => API.get('/admin/export/gtfs', { responseType: 'blob' });
